import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Users, FileText, Receipt, Wrench, X, ArrowRight, Loader2 } from 'lucide-react'
import { trpc } from '../../api/index.js'
import { useNav } from '../../stores/navigationStore'
import { cn } from '../../lib/utils'

interface GlobalSearchProps {
  onClose: () => void
}

type ResultItem =
  | { _type: 'client';     id: string; tip?: string; nume?: string | null; prenume?: string | null; denumireFirma?: string | null }
  | { _type: 'contract';   id: string; numarContract?: string | null; status?: string | null; clientId?: string | null }
  | { _type: 'factura';    id: string; serie?: string | null; numar?: number | null; status?: string | null; clientId?: string | null }
  | { _type: 'echipament'; id: string; codIntern?: string | null; denumire?: string | null; status?: string | null }

function itemLabel(item: ResultItem): string {
  switch (item._type) {
    case 'client':
      return item.tip === 'PJ'
        ? (item.denumireFirma ?? 'Firmă necunoscută')
        : [item.prenume, item.nume].filter(Boolean).join(' ') || 'Client necunoscut'
    case 'contract':
      return item.numarContract ?? `Contract ${item.id.slice(0, 8)}`
    case 'factura':
      return item.serie && item.numar != null
        ? `${item.serie}-${String(item.numar).padStart(4, '0')}`
        : `Factură ${item.id.slice(0, 8)}`
    case 'echipament':
      return [item.codIntern, item.denumire].filter(Boolean).join(' — ') || 'Echipament'
  }
}

function itemSub(item: ResultItem): string {
  switch (item._type) {
    case 'client':     return item.tip === 'PJ' ? 'Persoană juridică' : 'Persoană fizică'
    case 'contract':   return item.status ?? 'contract'
    case 'factura':    return item.status ?? 'factură'
    case 'echipament': return item.status ?? 'echipament'
  }
}

const TYPE_ICON = {
  client:     Users,
  contract:   FileText,
  factura:    Receipt,
  echipament: Wrench,
} as const

const TYPE_COLOR = {
  client:     'text-blue-400',
  contract:   'text-green-400',
  factura:    'text-purple-400',
  echipament: 'text-orange-400',
} as const

const TYPE_LABEL = {
  client:     'Client',
  contract:   'Contract',
  factura:    'Factură',
  echipament: 'Echipament',
} as const

export function GlobalSearch({ onClose }: GlobalSearchProps) {
  const [q, setQ] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { navigate } = useNav()
  const [cursor, setCursor] = useState(0)

  const searchQ = trpc.search.global.useQuery(
    { q: q.trim(), limit: 5 },
    { enabled: q.trim().length >= 2 },
  )

  const allResults: ResultItem[] = searchQ.data
    ? [
        ...searchQ.data.clienti,
        ...searchQ.data.contracte,
        ...searchQ.data.facturi,
        ...searchQ.data.echipamente,
      ]
    : []

  useEffect(() => { setCursor(0) }, [q])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const select = useCallback((item: ResultItem) => {
    switch (item._type) {
      case 'client':
        navigate('clienti', { clientId: item.id })
        break
      case 'contract':
        navigate('contracte', { contractId: item.id })
        break
      case 'factura':
        navigate('facturare', { facturaId: item.id })
        break
      case 'echipament':
        navigate('echipamente', { echipamentId: item.id })
        break
    }
    onClose()
  }, [navigate, onClose])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, allResults.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    if (e.key === 'Enter' && allResults[cursor]) select(allResults[cursor])
    if (e.key === 'Escape') onClose()
  }

  const hasResults = allResults.length > 0
  const isLoading  = q.trim().length >= 2 && searchQ.isLoading

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-800">
          {isLoading
            ? <Loader2 size={16} className="text-slate-400 animate-spin shrink-0" />
            : <Search size={16} className="text-slate-400 shrink-0" />
          }
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Caută client, contract, factură, echipament..."
            className="flex-1 bg-transparent text-white text-sm placeholder-slate-500 focus:outline-none"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-slate-500 hover:text-white">
              <X size={14} />
            </button>
          )}
          <kbd className="text-[10px] text-slate-500 px-1.5 py-0.5 border border-slate-700 rounded font-mono">Esc</kbd>
        </div>

        {/* Results */}
        {q.trim().length >= 2 && (
          <div className="max-h-80 overflow-y-auto">
            {!hasResults && !isLoading && (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                Niciun rezultat pentru „{q}"
              </div>
            )}

            {searchQ.data && (
              <>
                {(['clienti', 'contracte', 'facturi', 'echipamente'] as const).map(group => {
                  const items = searchQ.data[group] as ResultItem[]
                  if (!items.length) return null
                  const typeKey = items[0]._type
                  const Icon = TYPE_ICON[typeKey]
                  return (
                    <div key={group}>
                      <div className="px-4 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-950/40">
                        {TYPE_LABEL[typeKey]}
                      </div>
                      {items.map(item => {
                        const globalIdx = allResults.indexOf(item)
                        const isActive = cursor === globalIdx
                        return (
                          <button
                            key={item.id}
                            onClick={() => select(item)}
                            onMouseEnter={() => setCursor(globalIdx)}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                              isActive ? 'bg-slate-800' : 'hover:bg-slate-800/50',
                            )}
                          >
                            <Icon size={14} className={cn('shrink-0', TYPE_COLOR[typeKey])} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{itemLabel(item)}</p>
                              <p className="text-xs text-slate-500 capitalize">{itemSub(item)}</p>
                            </div>
                            {isActive && <ArrowRight size={13} className="text-slate-500 shrink-0" />}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}

        {/* Hints */}
        {q.trim().length < 2 && (
          <div className="px-4 py-5 space-y-3">
            <p className="text-xs text-slate-600 uppercase tracking-wider font-semibold">Navigare rapidă</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
              {([
                [Users,     'Clienți',     'clienti'],
                [FileText,  'Contracte',   'contracte'],
                [Receipt,   'Facturare',   'facturare'],
                [Wrench,    'Echipamente', 'echipamente'],
              ] as const).map(([Icon, label, page]) => (
                <button key={page} onClick={() => { navigate(page as any); onClose() }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 hover:text-slate-300 transition-colors text-left">
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-800 flex gap-4 text-[10px] text-slate-600">
          <span><kbd className="font-mono">↑↓</kbd> navighează</span>
          <span><kbd className="font-mono">Enter</kbd> deschide</span>
          <span><kbd className="font-mono">Esc</kbd> închide</span>
        </div>
      </div>
    </div>
  )
}
